/**
 * Aggregates dashboard notifications from stats, trades, inventory, billing, and account health.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { User } from '../../contexts/AuthContext'
import {
  getDashboardDismissedSnapshot,
  parseDashboardDismissedSnapshot,
  persistDashboardDismissedIds,
  readDashboardDismissedIds,
  subscribeDashboardDismissed,
} from '../../constants/dashboardAlertsDismiss'
import { fetchAccountOverview } from '../../services/account.service'
import { api } from '../../utils/api'
import type { OrganizationInfo } from '../Settings/Account.types'
import type { DashboardStatsPayload } from './dashboardTypes'
import { plural } from './dashboardFormat'

export type DashboardAlertSeverity = 'critical' | 'warning' | 'info'

export type DashboardAlert = {
  id: string
  severity: DashboardAlertSeverity
  title: string
  body: string
  cta: { label: string; href: string }
  dismissible: boolean
}

const STALE_DRAFT_TRADES_PATH = '/transactions?status=draft&type=instore_transaction'
const ALL_DRAFT_TRADES_PATH = '/transactions?status=draft'
/** Prices sync daily, so two days without one means it has stopped rather than not run yet. */
const PRICES_STALE_AFTER_MS = 48 * 60 * 60 * 1000

function readOrgInfo(resData: unknown): OrganizationInfo | null {
  const root = resData as { success?: boolean; data?: { organization?: OrganizationInfo; platforms?: unknown } }
  if (!root.success || !root.data?.organization) return null
  return {
    ...root.data.organization,
    platforms: (root.data.platforms || []) as OrganizationInfo['platforms'],
  }
}

function readPaginatedTotal(res: { data?: { pagination?: { total?: number } } }): number {
  const t = res.data?.pagination?.total
  return typeof t === 'number' ? t : 0
}

export type UseDashboardAlertsParams = {
  user: User | null
  stats: DashboardStatsPayload | null
}

export function useDashboardAlerts({ user, stats }: UseDashboardAlertsParams) {
  const dismissedRaw = useSyncExternalStore(
    subscribeDashboardDismissed,
    getDashboardDismissedSnapshot,
    () => '[]',
  )
  const dismissed = useMemo(() => parseDashboardDismissedSnapshot(dismissedRaw), [dismissedRaw])

  const canSeeBilling = ['owner', 'admin'].includes(user?.role || '')

  const accountQuery = useQuery({
    queryKey: ['dashboard', 'account-overview', 'alerts'],
    queryFn: async ({ signal }) => {
      const res = await fetchAccountOverview({ quiet: true, signal })
      return readOrgInfo(res.data)
    },
    enabled: Boolean(user),
  })

  const draftTradesQuery = useQuery({
    queryKey: ['dashboard', 'alerts', 'draft-trades-count'],
    queryFn: async ({ signal }) => {
      const res = await api.get('/trades', { signal, params: { status: 'draft', limit: 1, offset: 0 } })
      return readPaginatedTotal(res)
    },
    enabled: Boolean(user),
  })

  const dismissAlert = useCallback((id: string) => {
    const next = new Set(readDashboardDismissedIds())
    next.add(id)
    persistDashboardDismissedIds(next)
  }, [])

  const orgInfo = accountQuery.data ?? null

  const alerts = useMemo((): DashboardAlert[] => {
    const out: DashboardAlert[] = []
    const subWarnings = orgInfo?.subscription?.warnings ?? []
    const orgWarnings = orgInfo?.warnings ?? []

    if (canSeeBilling && subWarnings.length > 0) {
      const body =
        subWarnings.length === 1
          ? subWarnings[0]
          : `${subWarnings.slice(0, 2).join(' · ')}${subWarnings.length > 2 ? ` (+${subWarnings.length - 2} more)` : ''}`
      out.push({
        id: 'billing:summary',
        severity: 'critical',
        title: 'Billing or subscription',
        body,
        cta: { label: 'Review billing', href: '/settings?tab=billing' },
        dismissible: true,
      })
    } else if (canSeeBilling && orgWarnings.length > 0) {
      const nonDup = orgWarnings.filter((w) => !subWarnings.includes(w))
      if (nonDup.length > 0) {
        out.push({
          id: 'billing:org-warnings',
          severity: 'warning',
          title: 'Account notices',
          body: nonDup.slice(0, 2).join(' · '),
          cta: { label: 'Open account', href: '/settings?tab=overview' },
          dismissible: true,
        })
      }
    }

    if (user && user.isEmailVerified === false) {
      out.push({
        id: 'security:email',
        severity: 'critical',
        title: 'Verify your email',
        body: 'Confirm your email address to secure your account.',
        cta: { label: 'Account settings', href: '/settings?tab=profile' },
        dismissible: false,
      })
    }
    if (user && user.mfaEnabled === false) {
      out.push({
        id: 'security:mfa',
        severity: 'warning',
        title: 'Add two-factor authentication',
        body: 'MFA is not enabled for this account.',
        cta: { label: 'Security', href: '/settings?tab=security' },
        dismissible: true,
      })
    }

    const lastPriceSync = stats?.activity.lastSync
    if (lastPriceSync && Date.now() - new Date(lastPriceSync).getTime() > PRICES_STALE_AFTER_MS) {
      out.push({
        // Keyed to the sync so a later stall isn't hidden by having dismissed this one.
        id: `prices:stale:${lastPriceSync}`,
        severity: 'warning',
        title: 'Prices have stopped updating',
        body: `The last price update was ${new Date(lastPriceSync).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}.`,
        cta: { label: 'Integrations', href: '/settings?tab=integrations' },
        dismissible: true,
      })
    }

    const platforms = orgInfo?.platforms ?? []
    for (const cred of platforms) {
      const status = (cred.lastSyncStatus || '').toLowerCase()
      if (cred.lastSyncError && (status === 'failed' || status === 'partial')) {
        const platformName = cred.platform.charAt(0).toUpperCase() + cred.platform.slice(1)
        out.push({
          id: `integration:sync:${cred.id}`,
          severity: 'warning',
          title: `${platformName} sync needs attention`,
          body:
            cred.lastSyncError.length > 160
              ? `${cred.lastSyncError.slice(0, 157)}…`
              : cred.lastSyncError,
          cta: { label: 'Integrations', href: '/settings?tab=integrations' },
          dismissible: true,
        })
      }
    }

    const prefLowStock = user?.dashboardPreferences?.showLowStockWarnings !== false
    if (prefLowStock && stats && stats.inventory.lowStockCount > 0) {
      const n = stats.inventory.lowStockCount
      out.push({
        id: 'inventory:low-stock',
        severity: 'warning',
        title: 'Low stock',
        body: `${plural(n, 'product')} at 5 or fewer in stock.`,
        cta: { label: 'Review low stock', href: '/inventory/products?lowStock=1' },
        dismissible: true,
      })
    }

    const draftTotal = draftTradesQuery.data ?? 0
    const prefDraftTrades = user?.dashboardPreferences?.showDraftTradeWarnings !== false
    if (prefDraftTrades && draftTotal > 0) {
      const stale = stats?.concerns?.draftTradesStaleCount ?? 0
      const stalePart = stale > 0 ? `, ${stale.toLocaleString()} open more than a week` : ''
      out.push({
        id: 'trades:draft-open',
        severity: stale > 0 ? 'warning' : 'info',
        title: 'Draft trades',
        body: `${plural(draftTotal, 'open draft')}${stalePart}.`,
        cta: { label: stale > 0 ? 'Review stale drafts' : 'Review drafts', href: stale > 0 ? STALE_DRAFT_TRADES_PATH : ALL_DRAFT_TRADES_PATH },
        dismissible: true,
      })
    }

    if ((stats?.concerns?.manualPricesExpiring24hCount ?? 0) > 0) {
      const n = stats?.concerns?.manualPricesExpiring24hCount ?? 0
      out.push({
        id: 'pricing:manual-expiry',
        severity: 'info',
        title: 'Manual prices expiring',
        body: `${plural(n, 'manual price')} ${n === 1 ? 'expires' : 'expire'} within 24 hours.`,
        cta: { label: 'Review manual prices', href: '/inventory/products?manual=1' },
        dismissible: true,
      })
    }
    if ((stats?.concerns?.salesEndingTodayCount ?? 0) > 0) {
      const n = stats?.concerns?.salesEndingTodayCount ?? 0
      out.push({
        id: 'sales:ending-today',
        severity: 'info',
        title: 'Sales ending today',
        body: `${plural(n, 'sale')} ${n === 1 ? 'ends' : 'end'} today.`,
        cta: { label: 'Review expiring sales', href: '/inventory/products?onSale=1' },
        dismissible: true,
      })
    }

    const rank: Record<DashboardAlertSeverity, number> = { critical: 0, warning: 1, info: 2 }
    out.sort((a, b) => rank[a.severity] - rank[b.severity] || a.title.localeCompare(b.title))
    return out
  }, [canSeeBilling, draftTradesQuery.data, orgInfo, stats, user])

  /** Everything not dismissed, for the dashboard's list and the notification bell. */
  const bellDashboardAlerts = useMemo(
    () => alerts.filter((a) => !a.dismissible || !dismissed.has(a.id)),
    [alerts, dismissed],
  )

  return {
    alerts: bellDashboardAlerts,
    bellDashboardAlerts,
    dismissAlert,
    orgInfo,
  }
}
